import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * 响应式卡片网格
 * 移动端单列，平板双列，桌面多列
 */
export interface ResponsiveGridProps {
  children: React.ReactNode
  /** 桌面端列数 */
  columns?: 2 | 3 | 4 | 5 | 6
  /** 间距 */
  gap?: 'sm' | 'md' | 'lg'
  /** 自定义类名 */
  className?: string
}

export function ResponsiveGrid({
  children,
  columns = 4,
  gap = 'md',
  className,
}: ResponsiveGridProps) {
  const columnClasses = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
    5: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
    6: 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6',
  }

  const gapClasses = {
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
  }

  return (
    <div
      className={cn(
        'grid grid-cols-1',
        columnClasses[columns],
        gapClasses[gap],
        className
      )}
    >
      {children}
    </div>
  )
}

/**
 * 响应式列表容器
 * 包含筛选栏、列表内容、分页
 */
export interface ResponsiveListContainerProps {
  children: React.ReactNode
  className?: string
}

export function ResponsiveListContainer({
  children,
  className,
}: ResponsiveListContainerProps) {
  return <div className={cn('space-y-4 md:space-y-6', className)}>{children}</div>
}

/**
 * 列表页头部
 */
export interface ListHeaderProps {
  /** 标题 */
  title: string
  /** 描述 */
  description?: string
  /** 操作按钮 */
  actions?: React.ReactNode
  /** 自定义类名 */
  className?: string
}

export function ListHeader({ title, description, actions, className }: ListHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between',
        className
      )}
    >
      <div>
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">{title}</h1>
        {description && (
          <p className="text-sm text-muted-foreground sm:text-base">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  )
}

/**
 * 筛选栏
 * 移动端垂直堆叠，桌面端水平排列
 */
export interface FilterBarProps {
  children: React.ReactNode
  className?: string
}

export function FilterBar({ children, className }: FilterBarProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4',
        className
      )}
    >
      {children}
    </div>
  )
}

/**
 * 筛选项组
 */
export interface FilterGroupProps {
  children: React.ReactNode
  /** 标签 */
  label?: string
  className?: string
}

export function FilterGroup({ children, label, className }: FilterGroupProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {label && (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {label}
        </span>
      )}
      {children}
    </div>
  )
}

/**
 * 搜索框容器
 */
export interface SearchBoxProps {
  children: React.ReactNode
  className?: string
}

export function SearchBox({ children, className }: SearchBoxProps) {
  return (
    <div className={cn('flex-1 min-w-[200px] max-w-full sm:max-w-md', className)}>
      {children}
    </div>
  )
}

/**
 * 视图切换按钮组
 */
export interface ViewToggleProps {
  value: 'card' | 'list' | 'table'
  onChange: (value: 'card' | 'list' | 'table') => void
  options?: ('card' | 'list' | 'table')[]
  className?: string
}

export function ViewToggle({
  value,
  onChange,
  options = ['card', 'list'],
  className,
}: ViewToggleProps) {
  const icons = {
    card: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    ),
    list: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
      </svg>
    ),
    table: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
  }

  const labels = {
    card: '卡片视图',
    list: '列表视图',
    table: '表格视图',
  }

  return (
    <div className={cn('flex items-center gap-1 rounded-md border p-1', className)}>
      {options.map((option) => (
        <button
          key={option}
          onClick={() => onChange(option)}
          className={cn(
            'rounded p-1.5 transition-colors',
            value === option ? 'bg-muted' : 'hover:bg-muted/50'
          )}
          title={labels[option]}
        >
          {icons[option]}
        </button>
      ))}
    </div>
  )
}

/**
 * 分页容器
 */
export interface PaginationContainerProps {
  children: React.ReactNode
  /** 总数信息 */
  info?: React.ReactNode
  className?: string
}

export function PaginationContainer({
  children,
  info,
  className,
}: PaginationContainerProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        className
      )}
    >
      {info && (
        <p className="text-sm text-muted-foreground order-2 sm:order-1">
          {info}
        </p>
      )}
      <div className="flex items-center gap-2 order-1 sm:order-2">{children}</div>
    </div>
  )
}

/**
 * 响应式表格容器
 * 移动端水平滚动
 */
export interface ResponsiveTableProps {
  children: React.ReactNode
  className?: string
}

export function ResponsiveTable({ children, className }: ResponsiveTableProps) {
  return (
    <div className={cn('overflow-x-auto -mx-4 sm:mx-0', className)}>
      <div className="inline-block min-w-full align-middle px-4 sm:px-0">
        {children}
      </div>
    </div>
  )
}

/**
 * 移动端隐藏的列
 */
export function HiddenOnMobile({ children }: { children: React.ReactNode }) {
  return <span className="hidden sm:inline">{children}</span>
}

/**
 * 桌面端隐藏的元素
 */
export function HiddenOnDesktop({ children }: { children: React.ReactNode }) {
  return <span className="sm:hidden">{children}</span>
}
